import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Brush
} from 'recharts';
import { format, parseISO } from 'date-fns';

// Reference Monitor ("trailer") tab: daily means from the EPA-grade regulatory
// station. PM2.5 is overlaid against the PurpleAir county average (reference vs
// low-cost network); the other parameters are unique to this station.

const REF_COLOR = '#7c3aed';
const COUNTY_COLOR = '#3b82f6';
const PARAM_ORDER = ['pm25', 'o3', 'pm10', 'temp', 'rh', 'ws'];

const ReferenceTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white p-2 border rounded shadow-lg text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.filter(e => e.value != null).map((e, i) => (
        <div key={i} className="flex items-center gap-1" style={{ color: e.color }}>
          <span className="font-medium">{e.name}:</span>
          <span>{e.value?.toFixed(1)} {unit}</span>
        </div>
      ))}
    </div>
  );
};

const ReferenceMonitor = () => {
  const [data, setData] = useState(null);
  const [county, setCounty] = useState(null); // date -> PurpleAir county avg
  const [param, setParam] = useState('pm25');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load the static daily-aggregate JSON once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.PUBLIC_URL}/reference-daily.json`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) { setData(json); setError(null); }
      } catch (e) {
        if (!cancelled) setError(`Failed to load reference data: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // For the PM2.5 view, fetch the PurpleAir county daily series once to overlay.
  const fetchCounty = useCallback(async (start, end) => {
    try {
      const qs = new URLSearchParams({ type: 'county', startDate: start, endDate: end });
      const res = await fetch(`${process.env.REACT_APP_API_URL}/sensors/historical?${qs}`);
      if (!res.ok) return;
      const rows = await res.json();
      const map = {};
      rows.forEach(r => { map[r.date] = r.average; });
      setCounty(map);
    } catch {
      /* overlay is best-effort; reference line still renders without it */
    }
  }, []);

  useEffect(() => {
    if (param === 'pm25' && data && !county) {
      fetchCounty(data.meta.dateRange.start, data.meta.dateRange.end);
    }
  }, [param, data, county, fetchCounty]);

  const meta = data?.meta;
  const paramInfo = meta?.parameters?.[param];
  const unit = paramInfo?.unit || '';
  const showCounty = param === 'pm25';

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.daily
      .filter(r => r[param] != null)
      .map(r => ({
        date: r.date,
        timestamp: format(parseISO(r.date), 'MMM d, yyyy'),
        value: r[param],
        county: showCounty && county ? (county[r.date] ?? null) : null
      }));
  }, [data, param, county, showCounty]);

  // How well the PurpleAir network tracks the reference monitor: Pearson
  // correlation and mean difference over days where both have a value.
  const agreement = useMemo(() => {
    if (!showCounty) return null;
    const pairs = chartData.filter(d => d.county != null && d.value != null);
    if (pairs.length < 3) return null;
    const xs = pairs.map(d => d.county);   // PurpleAir
    const ys = pairs.map(d => d.value);    // reference
    const mean = a => a.reduce((s, v) => s + v, 0) / a.length;
    const mx = mean(xs), my = mean(ys);
    let cov = 0, vx = 0, vy = 0;
    for (let i = 0; i < pairs.length; i++) {
      const dx = xs[i] - mx, dy = ys[i] - my;
      cov += dx * dy; vx += dx * dx; vy += dy * dy;
    }
    const denom = Math.sqrt(vx * vy);
    if (denom === 0) return null;
    return { r: cov / denom, meanDiff: mx - my, days: pairs.length };
  }, [chartData, showCounty]);

  return (
    <div>
      <div className="flex justify-between items-center gap-2 mt-1">
        <h3 className="font-medium text-sm">
          Reference monitor{paramInfo ? ` — ${paramInfo.label}` : ''}
        </h3>
        <select
          value={param}
          onChange={e => setParam(e.target.value)}
          className="text-sm border rounded px-2 py-1"
        >
          {meta && PARAM_ORDER.filter(k => meta.parameters[k]).map(k => (
            <option key={k} value={k}>{meta.parameters[k].label}</option>
          ))}
        </select>
      </div>
      <p className="text-xs text-gray-600 mb-2">
        Daily averages from St. Mary's County's EPA-grade regulatory station.
        {showCounty
          ? ' Compare against the PurpleAir county average to see how closely the low-cost network tracks regulatory-grade measurements.'
          : ' This measurement is only available from the reference station.'}
        {meta && ` Coverage: ${meta.coverageDays?.[param] ?? 0} days (${meta.dateRange.start} to ${meta.dateRange.end}).`}
        {agreement && (
          <span className="text-gray-700">
            {' '}Agreement over {agreement.days} overlapping days: correlation r = {agreement.r.toFixed(2)};
            PurpleAir reads {Math.abs(agreement.meanDiff).toFixed(1)} µg/m³ {agreement.meanDiff >= 0 ? 'higher' : 'lower'} on average.
          </span>
        )}
      </p>

      <div className="h-[250px] bg-gray-50 p-3 rounded-lg">
        {loading ? (
          <div className="h-full flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        ) : (
          <ResponsiveContainer width="99%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={50} />
              <YAxis
                tick={{ fontSize: 11 }}
                label={{ value: unit, angle: -90, position: 'insideLeft', offset: 0, fontSize: 11 }}
              />
              <Tooltip content={<ReferenceTooltip unit={unit} />} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              {showCounty && (
                <Line
                  type="monotone" dataKey="county" name="PurpleAir county avg"
                  stroke={COUNTY_COLOR} strokeWidth={1.5} strokeDasharray="4 3"
                  dot={false} connectNulls isAnimationActive={false}
                />
              )}
              <Line
                type="monotone" dataKey="value" name={`Reference ${paramInfo?.label || ''}`}
                stroke={REF_COLOR} strokeWidth={2} dot={false} connectNulls isAnimationActive={false}
              />
              <Brush dataKey="timestamp" height={20} stroke={REF_COLOR} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ReferenceMonitor;
